import assert from 'assert';

import { compact, isArray } from 'lodash';

import { logger } from '../logger';
import { getEnvironment } from '../utils.mjs';

const { CLOUDINARY_CLOUD_NAME } = getEnvironment();

const HOST = 'res.cloudinary.com';
const IMAGE_PATH_PREFIX = 'image/upload';
const VIDEO_PATH_PREFIX = 'video/upload';
const SOCIAL_DELIVERY_TYPE_PATH_PREFIX = 'image';
const VERSION = 'v1';

const IMAGE_OPTS = {
  crop: 'c',
  background: 'b',
  angle: 'a',
  height: 'h',
  width: 'w',
  gravity: 'g',
  quality: 'q',
  format: 'f',
  flag: 'fl',
};

/*
 * Build the transform parameters path part of a cloudinary image URL.
 *
 * https://cloudinary.com/documentation/image_transformation_reference
 *
 * @param {Object} opts - transforms object.  If a option value is an array, multiple transforms will be created for the given key.
 *
 * The supported transforms are specified in the IMAGE_OPTS constant in this file.
 *
 * @example:
 *   {
 *      crop: 'fill',
 *      angle: 'exif',
 *      width: '100',
 *   }
 *
 * Would yield "a_exif,c_fill,w_100" .
 */
export const parseTransforms = (opts = {}) =>
  Object.keys(IMAGE_OPTS)
    .sort()
    .reduce((acc, key) => {
      if (opts[key]) {
        const vals = isArray(opts[key]) ? opts[key] : [opts[key]];
        vals.sort().forEach((val) => acc.push(`${IMAGE_OPTS[key]}_${val}`));
      }
      return acc;
    }, []);

/**
 * Build the path portion of a cloudinary image URL.
 *
 * @param {object} opts
 * @param {string} opts.cloudName - The cloudinary cloud name.
 * @param {string} opts.publicId - The image's public id.
 * @param {object} opts.transforms - Cloudinary transforms to apply to the image.  See the parseTransforms function for details.
 * @param {object} opts.resourceType - either image or video
 * @param {object} opts.version - cloudinary api version
 * @returns {object}
 */
const buildPath = ({
  publicId,
  transforms = {},
  cloudName = CLOUDINARY_CLOUD_NAME,
  resourceType = 'image',
  version = VERSION,
}) => {
  const pathPrefix =
    resourceType === 'video' ? VIDEO_PATH_PREFIX : IMAGE_PATH_PREFIX;
  assert(
    cloudName,
    'cloud name not specified in opts.cloudName or CLOUDINARY_CLOUD_NAME env'
  );
  assert(publicId, 'opts.publicId not specified');

  // transformations are optional so remove then when empty
  return compact([
    cloudName,
    pathPrefix,
    parseTransforms(transforms).join(','),
    version,
    publicId,
  ]).join('/');
};

/**
 * Special Cloudinary URL format for {@link https://cloudinary.com/documentation/social_media_profile_pictures|social media profile photos}.
 *
 * @param {object} opts
 * @param {string} opts.cloudName - The cloudinary cloud name.
 * @returns {object}
 */
const buildSocialDeliveryPath = (opts) => {
  const cloudName = opts.cloudName || CLOUDINARY_CLOUD_NAME;
  const pathPrefix = opts.pathPrefix || SOCIAL_DELIVERY_TYPE_PATH_PREFIX;

  assert(opts.socialDeliveryType, 'opts.socialDeliveryType not specified');
  assert(opts.socialIdentifier, 'opts.socialIdentifier not specified');

  return (
    [
      cloudName,
      pathPrefix,
      opts.socialDeliveryType,
      parseTransforms(opts.transforms).join(','),
      opts.socialIdentifier,
    ].join('/') + '.jpg'
  );
};

/**.
 * Build a complete cloudinary image URL.
 *
 * Some, but not all, available opts below.
 *

 * @param {object} opts
 * @param {string} opts.cloudName - The cloudinary cloud name.
 * @param {string} opts.socialDeliveryType -  If present, will use the "social delivery" version of a Cloudinary URL.
 * @param {string} opts.socialIdentifier - Id of the user in the social platform specified in socialDeliveryType.
 * @param {object} opts.transforms - Cloudinary transforms to apply to the image.  See the parseTransforms function for details.
 *
 * @example
 *
 * https://res.cloudinary.com/waybetter-staging/image/upload/a_exif,c_pad,h_350,w_300/v1/staging/falkor/game/ry5kgjrebpo5utcyccr3
 */
export const buildCloudinaryUrl = (opts) => {
  // Let buildPath gradefully fail. We don't care about it's exceptions and
  // we certainly don't want the entire page to crash.
  try {
    return `https://${opts.host || HOST}/${
      opts.socialDeliveryType ? buildSocialDeliveryPath(opts) : buildPath(opts)
    }`;
  } catch (error) {
    logger.error(`Failed to build cloudinary image url: ${error.message}`);
  }
};
